import { IonContent, IonPage, useIonLoading } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { QrActionTypes, QrObjectTypes } from '../constants/constants';
import { useSetRecoilState } from 'recoil';
import { qrRequestShareList } from '../features/checklist/listApi';
import { errorAlertMessageKeyState } from '../features/error/ErrorAlert';
import { SyncAborted, syncData } from '../sync/sync';
import { _list } from '../utils/state/model/implementations/ImplementationFactory';
import { useTranslation } from 'react-i18next';
import isPermittedNetworkConnected from '../sync/network/network';
import { usePublicReport } from '../features/publicReport/hooks/usePublicReport';
import { detectPublicLocale } from '../features/publicReport/localeUtils';
import PublicReportLoading from '../features/publicReport/components/PublicReportLoading';
import PublicCleaningReport from '../features/publicReport/components/PublicCleaningReport';
import PublicReportFallback from '../features/publicReport/components/PublicReportFallback';
import { Capacitor } from '@capacitor/core';

let qrLandingPageLoading = false;

interface QrLandingPageProps
  extends RouteComponentProps<{
    idType: string;
    id: string;
    action: string;
  }> {}

const QrLandingPage: React.FC<QrLandingPageProps> = ({
  match: {
    params: { idType, id, action },
  },
}) => {
  const setErrorAlertState = useSetRecoilState(errorAlertMessageKeyState);
  const history = useHistory();
  const { t } = useTranslation();
  const [presentLoading, dismissLoading] = useIonLoading();

  // Check if this is a public report request (action=sh, idType=list)
  // Skip public report flow if:
  // - ?internal=true is present (user redirected from PublicReportPage for share flow)
  // - Running on native platform (in-app QR scans should use share flow, not public report)
  const isInternalRedirect = window.location.search.includes('internal=true');
  const isNativePlatform = Capacitor.isNativePlatform();
  const isPublicReportRequest =
    action === QrActionTypes.SHARE && idType === QrObjectTypes.LIST && !isInternalRedirect && !isNativePlatform;
  const locale = useMemo(() => detectPublicLocale(), []);
  // Only fetch public report data if this is a public report request (not on native platforms)
  const {
    data: publicReportData,
    loading: publicReportLoading,
    error: publicReportError,
  } = usePublicReport(isPublicReportRequest ? id : '');

  // State to track if we should show the public report or fall through to list sharing
  const [showPublicReport, setShowPublicReport] = useState<boolean | null>(null);

  const handleError = () => {
    setErrorAlertState('errorAlert.list.unavailable');
    history.goBack();
  };

  // Effect to determine if we should show public report based on API response
  useEffect(() => {
    if (isPublicReportRequest) {
      if (!publicReportLoading) {
        if (publicReportData && !publicReportError) {
          // Public report API returned data - show the report
          setShowPublicReport(true);
        } else {
          // Public report API returned error/404 - show fallback
          setShowPublicReport(false);
        }
      }
    }
  }, [isPublicReportRequest, publicReportLoading, publicReportData, publicReportError]);

  useEffect(() => {
    // Skip the list sharing flow if this is a public report request
    if (isPublicReportRequest) {
      return;
    }

    console.log('QrLandingPage', idType, id, action);

    const processId = async () => {
      if (qrLandingPageLoading) {
        console.log('Already running so returning early');
        return;
      }
      qrLandingPageLoading = true;

      if (!idType || !id || !action) {
        console.error('Missing required route parameters');
        handleError();
      }

      // await the presentation of the loading message or it may not be loaded when we try to dismiss it
      await presentLoading({
        message: t('loading.generic'),
      });

      if (idType === QrObjectTypes.LIST) {
        // Check if the user is offline
        if (!(await isPermittedNetworkConnected())) {
          // Check if the user has the list downloaded
          const listExists = await _list.doesListExist(id);
          if (listExists) {
            console.log('List is on device and user is offline');
            history.replace(`/list/${id}`);
          } else {
            console.warn('List is not on device and user is offline');
            handleError();
          }
        } else {
          try {
            console.log('Requesting access to list');
            const success = await qrRequestShareList(id);
            if (success) {
              await syncList();

              history.replace(`/list/${id}`);
            } else {
              console.error('Failure response requesting list');
              handleError();
            }
          } catch (error) {
            console.error('Error requesting list', error);
            handleError();
          }
        }
      }
    };
    processId();

    return () => {
      dismissLoading();
      qrLandingPageLoading = false;
    };
  }, [isPublicReportRequest]);

  const syncList = async () => {
    const MAX_SYNC_ATTEMPTS = 10;
    const DELAY_BETWEEN_ATTEMPTS_IN_MS = 500;
    const SYNC_ABORTED_MESSAGE = 'sync was aborted';

    for (let attempts = 0; attempts < MAX_SYNC_ATTEMPTS; attempts++) {
      try {
        console.log(`Syncing list after scanning QR code. Attempt ${attempts + 1}/${MAX_SYNC_ATTEMPTS}`);
        const syncFinished = await syncData({
          forceSyncLists: id ? [id] : [],
        });

        if (syncFinished) {
          console.log('Finished syncing list after scanning QR code');
          return;
        } else {
          console.log('Sync was aborted');
          throw new SyncAborted(SYNC_ABORTED_MESSAGE);
        }
      } catch (error: any) {
        if (error.message !== SYNC_ABORTED_MESSAGE) {
          console.error('Error syncing data after scanning QR code', error);
        }

        if (attempts === MAX_SYNC_ATTEMPTS - 1) {
          console.warn('Max sync attempts reached');
          throw error;
        } else {
          console.log('Attempt syncing again after delay');
          await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_ATTEMPTS_IN_MS));
        }
      }
    }
  };

  // Render public report if this is a public report request
  if (isPublicReportRequest) {
    // Still loading the public report API
    if (publicReportLoading || showPublicReport === null) {
      return (
        <IonPage>
          <IonContent>
            <PublicReportLoading locale={locale} />
          </IonContent>
        </IonPage>
      );
    }

    // Public report data available - show the report
    if (showPublicReport && publicReportData) {
      return (
        <IonPage>
          <IonContent>
            <PublicCleaningReport report={publicReportData} locale={locale} />
          </IonContent>
        </IonPage>
      );
    }

    // Public report not available - show fallback
    return (
      <IonPage>
        <IonContent>
          <PublicReportFallback locale={locale} />
        </IonContent>
      </IonPage>
    );
  }

  // Default: show loading spinner for list sharing flow
  return <IonPage>{/* just show the loading spinner */}</IonPage>;
};

export default QrLandingPage;
